import { useEffect, useRef, useState } from "react";
import { Reorder } from "framer-motion";
import { toast } from "sonner";
import { GripVertical, Pencil, Trash2, Eye, EyeOff, PlusCircle, ImageUp, Loader2 } from "lucide-react";
import { api, mediaUrl, formatApiError } from "@/lib/api";

const EMPTY = { name: "", description: "", cover_path: "", status: "published" };

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/categories/${editingId}`, form);
        toast.success("Category updated");
      } else {
        await api.post("/admin/categories", form);
        toast.success("Category created");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const edit = (cat) => {
    setEditingId(cat.id);
    setForm({ name: cat.name, description: cat.description, cover_path: cat.cover_path, status: cat.status });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (cat) => {
    const warning =
      cat.item_count > 0
        ? `Delete “${cat.name}”? It contains ${cat.item_count} work item(s) which will become uncategorized.`
        : `Delete “${cat.name}”?`;
    if (!window.confirm(warning)) return;
    try {
      await api.delete(`/admin/categories/${cat.id}`);
      toast.success("Category deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleStatus = async (cat) => {
    try {
      await api.put(`/admin/categories/${cat.id}`, {
        name: cat.name,
        description: cat.description,
        cover_path: cat.cover_path,
        status: cat.status === "published" ? "hidden" : "published",
      });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const onReorder = async (next) => {
    setCategories(next);
    try {
      await api.put("/admin/categories/reorder", { ids: next.map((c) => c.id) });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const r = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, cover_path: r.data.files[0].storage_path }));
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const inputCls =
    "w-full bg-surface-white border border-charcoal-900/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-copper-600";

  return (
    <div>
      <h1 className="font-serif text-3xl text-charcoal-900 mb-1">Categories</h1>
      <p className="text-sm text-charcoal-500 mb-8">Drag to reorder. Published categories appear on the public portfolio.</p>

      <form data-testid="category-form" onSubmit={save} className="bg-surface-white border border-charcoal-900/10 rounded-2xl p-6 mb-8 flex flex-col gap-4">
        <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-copper-600">
          {editingId ? "Edit Category" : "Add Category"}
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <input
            data-testid="category-name-input"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Category name — e.g. Packaging"
            aria-label="Category name"
            className={inputCls}
          />
          <select
            data-testid="category-status-select"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            aria-label="Category status"
            className={inputCls}
          >
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
        <textarea
          data-testid="category-description-input"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Short description (optional)"
          aria-label="Category description"
          rows={2}
          className={`${inputCls} resize-none`}
        />
        <div className="flex items-center gap-4">
          <button
            type="button"
            data-testid="category-cover-upload"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-full border border-charcoal-900/20 text-charcoal-700 text-xs font-medium px-4 py-2.5 hover:border-copper-600 hover:text-copper-600 transition-colors"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageUp className="w-3.5 h-3.5" />}
            {form.cover_path ? "Change cover image" : "Upload cover image"}
          </button>
          {form.cover_path && (
            <img src={mediaUrl(form.cover_path)} alt="Cover preview" className="w-14 h-14 rounded-lg object-cover border border-charcoal-900/10" />
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => uploadCover(e.target.files?.[0])}
            data-testid="category-cover-file-input"
          />
        </div>
        <div className="flex gap-3">
          <button
            data-testid="category-save-button"
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-charcoal-900 text-parchment text-sm font-medium px-6 py-3 hover:bg-copper-600 disabled:opacity-60 transition-colors"
          >
            <PlusCircle className="w-4 h-4" />
            {saving ? "Saving..." : editingId ? "Save Changes" : "Create Category"}
          </button>
          {editingId && (
            <button
              type="button"
              data-testid="category-cancel-edit"
              onClick={() => { setEditingId(null); setForm(EMPTY); }}
              className="rounded-full border border-charcoal-900/20 text-sm font-medium px-6 py-3 hover:border-copper-600 hover:text-copper-600 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <Reorder.Group axis="y" values={categories} onReorder={onReorder} className="flex flex-col gap-3" data-testid="admin-category-list">
        {categories.map((cat) => (
          <Reorder.Item
            key={cat.id}
            value={cat}
            data-testid={`admin-category-row-${cat.slug}`}
            className="bg-surface-white border border-charcoal-900/10 rounded-xl p-4 flex items-center gap-4 cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-4 h-4 text-charcoal-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-charcoal-900 truncate">{cat.name}</p>
              <p className="text-xs text-charcoal-500 mt-0.5 truncate">
                /portfolio/{cat.slug} · {cat.item_count} work{cat.item_count === 1 ? "" : "s"}
              </p>
            </div>
            <span
              className={`hidden sm:inline-block font-mono text-[10px] uppercase tracking-wide border rounded-full px-2.5 py-1 ${
                cat.status === "published"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-charcoal-900/5 text-charcoal-500 border-charcoal-900/15"
              }`}
            >
              {cat.status}
            </span>
            <div className="flex items-center gap-1">
              <button data-testid={`category-publish-${cat.slug}`} onClick={() => toggleStatus(cat)} aria-label="Toggle visibility" className="p-2 rounded-lg text-charcoal-400 hover:text-copper-600 transition-colors">
                {cat.status === "published" ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button data-testid={`category-edit-${cat.slug}`} onClick={() => edit(cat)} aria-label={`Edit ${cat.name}`} className="p-2 rounded-lg text-charcoal-400 hover:text-copper-600 transition-colors">
                <Pencil className="w-4 h-4" />
              </button>
              <button data-testid={`category-delete-${cat.slug}`} onClick={() => remove(cat)} aria-label={`Delete ${cat.name}`} className="p-2 rounded-lg text-charcoal-400 hover:text-red-600 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </Reorder.Item>
        ))}
      </Reorder.Group>
    </div>
  );
}
